import { useState, useEffect, useRef } from 'react';
import { useIsMobile } from '../lib/useIsMobile.js';

// ─── A confirm step for destructive actions ──────────────────────────────────
// The in-app replacement for window.confirm() where a slip costs someone their
// access (Logins "Remove access" / "Delete", Settings → Team "Remove", a user's
// "Remove from this client"). Native confirm() can't name things in bold, can't
// show the server's refusal (e.g. the last-Owner guard), and is a tiny system
// box on a phone.
//
// Deliberate choices:
//   • Focus lands on CANCEL, so a stray Enter never destroys anything.
//   • `requireText` = type-to-confirm (the full login Delete): the destructive
//     button stays disabled until the typed text matches (case-insensitive).
//   • `onConfirm` may be async: both buttons lock while it runs, and a thrown
//     error is shown IN the dialog (it stays open) instead of a bare alert().
//     Resolve → the caller closes it (usually by clearing its state).
//   • Mobile-first: a bottom sheet with full-width stacked buttons on a phone,
//     a centred card on desktop; every tap target ≥ 44px.
//   • Esc / backdrop tap = Cancel (ignored while busy).
export default function ConfirmDialog({ title, children, confirmLabel = 'Confirm', busyLabel, cancelLabel = 'Cancel', tone = 'danger', requireText = '', onConfirm, onCancel }) {
  const isMobile = useIsMobile();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [typed, setTyped] = useState('');
  const cancelRef = useRef(null);
  const busyRef = useRef(false);
  busyRef.current = busy;

  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape' && !busyRef.current) onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const need = String(requireText || '').trim();
  const matched = !need || typed.trim().toLowerCase() === need.toLowerCase();
  const confirm = async () => {
    if (busy || !matched) return;
    setError(''); setBusy(true);
    try { await onConfirm?.(); }
    catch (e) { setError(e?.message || 'Something went wrong — nothing was changed.'); setBusy(false); return; }
    setBusy(false);
  };
  const danger = tone === 'danger';

  return (
    <div style={{ ...overlay, alignItems: isMobile ? 'flex-end' : 'center', padding: isMobile ? 0 : 16 }} onClick={() => { if (!busy) onCancel?.(); }}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-body"
        style={{ ...sheet, ...(isMobile ? sheetMobile : sheetDesktop) }} onClick={(e) => e.stopPropagation()}>
        <h2 id="confirm-dialog-title" style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px', wordBreak: 'break-word' }}>{title}</h2>
        <div id="confirm-dialog-body" style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text)', wordBreak: 'break-word' }}>{children}</div>
        {need && (
          <label style={{ display: 'block', marginTop: 14 }}>
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Type <b style={{ color: 'var(--text)' }}>{need}</b> to confirm</span>
            <input style={inp} value={typed} onChange={(e) => setTyped(e.target.value)} disabled={busy}
              autoComplete="off" autoCapitalize="off" spellCheck={false} aria-label={`Type ${need} to confirm`}
              onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }} />
          </label>
        )}
        {error && <div role="alert" style={{ color: 'var(--error)', fontSize: 13, marginTop: 12, lineHeight: 1.45 }}>{error}</div>}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button ref={cancelRef} type="button" style={ghostBtn} onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          <button type="button" style={{ ...goBtn, background: danger ? 'var(--error)' : 'var(--brand)', opacity: busy || !matched ? 0.55 : 1, cursor: busy || !matched ? 'not-allowed' : 'pointer' }}
            onClick={confirm} disabled={busy || !matched}>
            {busy ? (busyLabel || 'Working…') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', zIndex: 1100 };
const sheet = { background: 'var(--card)', color: 'var(--text)', width: '100%', boxSizing: 'border-box', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto' };
const sheetMobile = { borderRadius: '18px 18px 0 0', padding: '20px 18px calc(18px + env(safe-area-inset-bottom, 0px))' };
const sheetDesktop = { borderRadius: 16, padding: '22px 24px', maxWidth: 440 };
const inp = { display: 'block', marginTop: 6, padding: '10px 12px', minHeight: 44, border: '1.5px solid var(--hairline)', borderRadius: 10, fontSize: 16, outline: 'none', background: 'var(--card)', color: 'var(--text)', width: '100%', boxSizing: 'border-box' };
const btn = { padding: '10px 18px', minHeight: 44, borderRadius: 980, fontSize: 14, fontWeight: 600 };
const ghostBtn = { ...btn, background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--hairline)', cursor: 'pointer' };
const goBtn = { ...btn, color: '#fff', border: 'none' };
